import React, { useState, useMemo } from "react";
import { Icon } from "rhombus-react";

import useFTMessage from "hooks/Notification";

import { getFilenameExtension } from "utils/common";

import "./UploadListMessage.css";

const ASSET_ICONS = {
	image: "assetImage",
	video: "assetVideo",
	richload: "assetRichload",
	audio: "assetAudio",
};

// This should be maintained to have an entry for everything in UPLOAD_CREATIVE_FILE_TYPES
const ASSET_ICON_BY_EXTENSION = {
	zip: ASSET_ICONS.richload,
	png: ASSET_ICONS.image,
	jpg: ASSET_ICONS.image,
	jpeg: ASSET_ICONS.image,
	gif: ASSET_ICONS.image,
	svg: ASSET_ICONS.image,
};

function getIcon(filename){
	let extension = getFilenameExtension(filename);
	return ASSET_ICON_BY_EXTENSION[extension];
}

function FileProgress(props){
	const {
		icon,
		filename,
		loaded,
		total,
		error,
	} = props;

	let iconName = icon;
	let iconClass = "waiting";
	let progressClass = "upload-list-message upload-progress";

	let $progress = <progress className={progressClass} key="progress" value={loaded} max={total} />;

	if(error){
		iconName = "fas fa-times-circle";
		iconClass = "is-error";
		$progress = <progress className={`${progressClass} is-error`} key="progress" value={1} max={1} />;
	} else {
		if((loaded > 0) && (loaded < total)){
			iconName = "far fa-sync-alt";
			iconClass = "is-loading-something";
		}
		if(loaded === total){
			iconName = "success";
			iconClass = "is-success";
		}
	}

	return [
		<div key="icon" className="upload-list-message icon"><Icon name={iconName} className={iconClass} /></div>,
		<div key="filename" className="upload-list-message filename">
			<span className="name-overflow" title={filename}>{filename}</span>
			{ error ? <span className="error-message">{` - ${error}`}</span> : null }
		</div>,
		$progress,
		<b key="clear" />
	];
}

export default function UploadListMessage(props){
	const {
		id,
		status,
		iconInfo,
		timestamp,
		prettyTime,
		clearMessage,

		data: {
			header,
			assetType,
			libraryName,
			files,
			// errors,
			// progress,
		} = {},
	} = props;

	let { icon, title } = iconInfo || {};
	let [progress, setProgress] = useState(props.data.progress || {});
	let [errors, setErrors] = useState(props.data.errors || {});

	if(/in progress/i.test(title)){
		// This overrides the default icon that MessageItem sets for "In Progress" status.
		icon = "fas fa-cloud-upload";
	}

	useFTMessage((event) => {
		/*
			There's an issue where just updating a message doesn't seem to reliably propagate
			changes to message components, so we subcribe to events and react to them,
			specifically to update the upload progress and errors values
		*/
		let message = event.detail?.message || {};

		if((message.id !== id) || (message.status === "Success")){ return; }

		let eventData = message?.data || {};
		let eventProgress = eventData.progress || {};
		let eventErrors = eventData.errors || {};

		setProgress(eventProgress);
		setErrors(eventErrors);
	});

	let $fileList = useMemo(() => {
		return files.map((file) => {
			let { filename } = file;
			let { loaded = 0, total = 1 } = progress?.[filename] || {};
			let error = errors?.[filename] || null;
			let progressProps = {
				icon: ASSET_ICONS[assetType] || getIcon(filename),
				filename,
				loaded,
				total,
				error,
			};
			return <FileProgress key={filename} {...progressProps} />;
		});
	}, [files, progress, errors]);

	let $clear = <b key="clear" />;
	if(status !== "In Progress"){
		$clear = <Icon key="clear" name="clear" iconStyle="fal" className="clear-message" title="Clear Message" onClick={clearMessage} />;
	}

	return [
		<i key="status-icon" className={`ft-ic status-icon ${icon}`} title={title} />
		, <div key="body" className="message-body">
			<b>{header}</b>
		</div>

		, $clear
		// Spacer item, because it's display: grid
		, <b key="empty" />

		, <span key="footer" className="message-footer name-overflow" title={libraryName}>{libraryName}</span>
		, <time key="time" className="message-time" dateTime={timestamp}>{prettyTime}</time>
		, $fileList
	];
}

UploadListMessage.displayName = "UploadListMessage";

